<template>
  <div class="jim-scheduling-container whatif-page">
    <div class="page-header-row">
      <div></div>
      <h1 class="page-heading">WHAT-IF ANALYSIS</h1>
      <div></div>
    </div>

    <div class="main-content">
      <div class="left-side-section">
        <div class="control">
          <label>Start Date</label>
          <input v-model="startDate" type="date" />
        </div>

        <div class="control">
          <label>End Date</label>
          <input v-model="endDate" type="date" />
        </div>

        <div class="control">
          <label>Location</label>
          <select v-model="selectedLocation">
            <option value="ALL">All Locations</option>
            <option value="F">Fresno</option>
            <option value="D">Denver</option>
            <option value="K">Knoxville</option>
            <option value="L">Lakeland</option>
          </select>
        </div>

        <div class="panel assumptions-panel">
          <div class="assumption-title">What-If Modifiers</div>

          <div class="control">
            <label>Revenue Price Change %</label>
            <input v-model.number="assumptions.revenuePricePct" type="number" step="0.01" />
          </div>

          <div class="control">
            <label>Revenue Volume Change %</label>
            <input v-model.number="assumptions.revenueVolumePct" type="number" step="0.01" />
          </div>

          <div class="control">
            <label>COGs Change %</label>
            <input v-model.number="assumptions.cogsPct" type="number" step="0.01" />
          </div>
        </div>

        <button class="plan-it-btn" @click="loadSummary">
          Run Analysis
        </button>

        <button class="plan-it-btn" :disabled="!loaded" @click="resetModifiers">
          Reset Modifiers
        </button>

        <button class="plan-it-btn" :disabled="!loaded" @click="openPrintView">
            Print
        </button>

        <div v-if="error" class="panel error-box">
          {{ error }}
        </div>
      </div>

      <div class="right-side-section">
        <div v-if="loaded" class="summary-bar">
          <div class="summary-item">
            <div class="summary-label">Rows</div>
            <div class="summary-value">{{ formatNumber(rowCount) }}</div>
          </div>

          <div class="summary-item">
            <div class="summary-label">Months</div>
            <div class="summary-value">{{ monthCount }}</div>
          </div>

          <div class="summary-item">
            <div class="summary-label">GM %</div>
            <div class="summary-value">{{ formatPct(summary.gmPct) }}</div>
          </div>

          <div class="summary-item">
            <div class="summary-label">Op Income</div>
            <div class="summary-value">{{ formatMoney(summary.operatingIncome) }}</div>
          </div>

          <div class="summary-item">
            <div class="summary-label">% of Net Sales</div>
            <div class="summary-value">{{ formatPct(summary.operatingIncomePct) }}</div>
          </div>
        </div>

        <div v-if="loaded" class="sheet-page panel">
          <div class="sheet-page-header">
            <h2>{{ headerLocation }} • {{ startDate }} to {{ endDate }}</h2>
            <div class="small-muted">
              Price {{ assumptions.revenuePricePct }}% • Volume {{ assumptions.revenueVolumePct }}% • COGs {{ assumptions.cogsPct }}%
            </div>
          </div>

          <div class="table-scroll">
            <table class="price-table whatif-table">
              <thead>
                <tr>
                  <th>Description</th>
                  <th>%</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Gross Sales</td>
                  <td></td>
                  <td>{{ formatMoney(summary.grossSales) }}</td>
                </tr>
                <tr>
                  <td>Rebates</td>
                  <td>{{ assumptions.rebatesPct.toFixed(2) }}%</td>
                  <td>({{ formatMoney(summary.rebates) }})</td>
                </tr>
                <tr>
                  <td>Cash Discounts</td>
                  <td>{{ assumptions.cashDiscountPct.toFixed(2) }}%</td>
                  <td>({{ formatMoney(summary.cashDiscounts) }})</td>
                </tr>
                <tr class="subtotal-row">
                  <td>Net Sales</td>
                  <td></td>
                  <td>{{ formatMoney(summary.netSales) }}</td>
                </tr>

                <tr>
                  <td>Gross COGs</td>
                  <td></td>
                  <td>{{ formatMoney(summary.grossCogs) }}</td>
                </tr>
                <tr>
                  <td>Purchase Discounts</td>
                  <td>{{ assumptions.purchaseDiscountPct.toFixed(2) }}%</td>
                  <td>{{ formatMoney(summary.purchaseDiscounts) }}</td>
                </tr>
                <tr>
                  <td>Vendor Rebates</td>
                  <td>{{ assumptions.vendorRebatesPct.toFixed(2) }}%</td>
                  <td>{{ formatMoney(summary.vendorRebates) }}</td>
                </tr>
                <tr>
                  <td>Variances To Standard</td>
                  <td>{{ assumptions.variancesToStandardPct.toFixed(2) }}%</td>
                  <td>{{ formatMoney(summary.variancesToStandard) }}</td>
                </tr>
                <tr>
                  <td>Direct Labor &amp; OH Variance</td>
                  <td>{{ assumptions.directLaborOhVariancePct.toFixed(2) }}%</td>
                  <td>({{ formatMoney(summary.directLaborOhVariance) }})</td>
                </tr>
                <tr class="subtotal-row">
                  <td>Net COGs</td>
                  <td></td>
                  <td>{{ formatMoney(summary.netCogs) }}</td>
                </tr>

                <tr class="subtotal-row emphasis-row">
                  <td>GM</td>
                  <td>{{ formatPct(summary.gmPct) }}</td>
                  <td>{{ formatMoney(summary.gm) }}</td>
                </tr>

                <tr>
                  <td>Fixed Expenses</td>
                  <td>{{ monthCount }} mo</td>
                  <td>({{ formatMoney(summary.fixedExpenses) }})</td>
                </tr>
                <tr>
                  <td>Commissions</td>
                  <td>{{ assumptions.commissionsPct.toFixed(2) }}%</td>
                  <td>({{ formatMoney(summary.commissions) }})</td>
                </tr>
                <tr>
                  <td>Variable Expenses</td>
                  <td>{{ assumptions.variableExpensesPct.toFixed(2) }}%</td>
                  <td>({{ formatMoney(summary.variableExpenses) }})</td>
                </tr>
                <tr>
                  <td>Overhead Absorbed</td>
                  <td>{{ assumptions.overheadAbsorbedPct.toFixed(2) }}%</td>
                  <td>{{ formatMoney(summary.overheadAbsorbed) }}</td>
                </tr>

                <tr class="subtotal-row emphasis-row">
                  <td>Operating Income</td>
                  <td>{{ formatPct(summary.operatingIncomePct) }}</td>
                  <td>{{ formatMoney(summary.operatingIncome) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-else class="panel notes-panel">
          <div class="notes-title">Instructions</div>
          <div class="notes-body">
            Pick a date range and location, then enter price / volume / COGs modifiers.
            The base data comes from Macola sales history using tot_sls_amt and tot_cost.
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "WhatIfAnalysis",

  data() {
    return {
      startDate: "",
      endDate: "",
      selectedLocation: "ALL",
      loaded: false,
      error: null,
      rowCount: 0,
      monthCount: 0,
      apiSummary: null,
      assumptions: {
        rebatesPct: 5.0,
        cashDiscountPct: 1.5,
        purchaseDiscountPct: 1.0,
        commissionsPct: 1.7,
        vendorRebatesPct: 2.5,
        variancesToStandardPct: 0.50,
        directLaborOhVariancePct: 2.0,
        fixedExpensesPerMonth: 900000,
        overheadAbsorbedPct: 5.85,
        variableExpensesPct: 9.0,
        revenuePricePct: 0.0,
        revenueVolumePct: 0.0,
        cogsPct: 0.0
      },
      base: {
        grossSales: 0,
        grossCogs: 0
      }
    };
  },

  computed: {
    headerLocation() {
      const map = {
        ALL: "All Locations",
        F: "Fresno",
        D: "Denver",
        K: "Knoxville",
        L: "Lakeland"
      };
      return map[this.selectedLocation] || this.selectedLocation;
    },

    summary() {
      return this.apiSummary || {
        grossSales: 0,
        rebates: 0,
        cashDiscounts: 0,
        netSales: 0,
        grossCogs: 0,
        purchaseDiscounts: 0,
        vendorRebates: 0,
        variancesToStandard: 0,
        directLaborOhVariance: 0,
        netCogs: 0,
        gm: 0,
        gmPct: 0,
        fixedExpenses: 0,
        commissions: 0,
        variableExpenses: 0,
        overheadAbsorbed: 0,
        operatingIncome: 0,
        operatingIncomePct: 0
      };
    }
  },

  mounted() {
    const today = new Date();
    const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    this.startDate = this.toDateInput(firstOfMonth);
    this.endDate = this.toDateInput(today);
  },

  methods: {
    async loadSummary() {
        this.error = null;
        this.loaded = false;
        this.apiSummary = null;

        try {
            const { data } = await axios.get("/whatIfSummary", {
            params: {
                startDate: this.startDate,
                endDate: this.endDate,
                location: this.selectedLocation,
                revenuePricePct: this.assumptions.revenuePricePct,
                revenueVolumePct: this.assumptions.revenueVolumePct,
                cogsPct: this.assumptions.cogsPct
            }
            });

            this.rowCount = data.rowCount || 0;
            this.monthCount = data.monthCount || 0;
            this.base = data.base || { grossSales: 0, grossCogs: 0 };
            this.apiSummary = data.summary || null;

            this.assumptions = {
            ...this.assumptions,
            ...(data.assumptions || {})
            };

            this.loaded = true;
        } catch (err) {
            console.error("loadSummary error:", err);
            this.error = err?.response?.data?.error || "Failed to load What-If summary.";
        }
    },
    resetModifiers() {
      this.assumptions.revenuePricePct = 0;
      this.assumptions.revenueVolumePct = 0;
      this.assumptions.cogsPct = 0;
    },

    toDateInput(d) {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },

    formatMoney(v) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0
      }).format(Number(v || 0));
    },

    formatPct(v) {
      return `${(Number(v || 0) * 100).toFixed(1)}%`;
    },

    openPrintView() {
        if (!this.loaded || !this.summary) return;

        const payload = {
            meta: {
            generatedAt: new Date().toISOString(),
            location: this.headerLocation,
            startDate: this.startDate,
            endDate: this.endDate,
            assumptions: { ...this.assumptions }
            },
            rowCount: this.rowCount,
            monthCount: this.monthCount,
            summary: { ...this.summary }
        };

        sessionStorage.setItem(
            "pablo_whatif_print_payload",
            JSON.stringify(payload)
        );

        window.open("/print-whatif", "_blank");
   },

    formatNumber(v) {
      return new Intl.NumberFormat("en-US").format(Number(v || 0));
    }
  }
};
</script>

<style scoped>
.whatif-page {
  background-color: #0b0101;
  min-height: 100vh;
  padding: 14px;
}

.jim-scheduling-container {
  font-family: Garamond, serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #cbbfbf;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  background-color: #0b0101;
  min-height: 100vh;
}

.page-header-row {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  margin-bottom: 10px;
}

.page-heading {
  font-family: "Cormorant Garamond", serif;
  font-size: 36px;
  color: #cbbfbf;
  margin: 0;
  text-align: center;
}

.main-content {
  display: grid;
  grid-template-columns: 220px 1fr;
  gap: 12px;
  width: 100%;
  padding: 0 14px;
  box-sizing: border-box;
  align-items: start;
}

.left-side-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin-top: 60px;
}

.right-side-section {
  width: 100%;
  min-width: 0;
}

.panel {
  background-color: #0b0101;
  border: 1px solid #888;
  border-radius: 10px;
  padding: 10px 12px;
  text-align: left;
}

.control {
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: left;
  width: 100%;
}

.control label {
  font-size: 13px;
  color: #cbbfbf;
  font-weight: 600;
}

.control input,
.control select {
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid #444;
  background-color: #111;
  color: #cbbfbf;
  font-family: inherit;
  width: 100%;
  box-sizing: border-box;
}

.assumptions-panel {
  width: 100%;
}

.assumption-title {
  font-weight: bold;
  margin-bottom: 10px;
  color: #fff;
}

.plan-it-btn {
  border-radius: 10px;
  padding: 8px 16px;
  width: 170px;
  background-color: #f3f4f6;
  border: 1px solid #cfd3d8;
  color: #333;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  height: 38px;
}

.plan-it-btn:hover:not(:disabled) {
  background-color: #e5e7eb;
}

.plan-it-btn:disabled {
  background-color: #e5e7eb;
  color: #888;
  cursor: not-allowed;
}

.error-box {
  border-color: #aa4444;
  background: #2a0b0b;
  color: #fca5a5;
  width: 100%;
}

.summary-bar {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #888;
  margin: 0 0 12px;
  background-color: #708090a4;
  flex-wrap: wrap;
  width: 100%;
  box-sizing: border-box;
}

.summary-item {
  min-width: 140px;
  flex: 1 1 140px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.summary-label {
  font-size: 18px;
  opacity: 0.85;
  line-height: 1.1;
}

.summary-value {
  font-size: 24px;
  font-weight: bold;
  color: #fff;
  line-height: 1.1;
  margin-top: 2px;
}

.sheet-page {
  background-color: #708090a4;
  border-radius: 10px;
  border: 1px solid #888;
}

.sheet-page-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 8px;
}

.sheet-page-header h2 {
  margin: 0;
  font-family: "Cormorant Garamond", Garamond, serif;
  font-size: 22px;
  color: #fff;
}

.small-muted {
  font-size: 12px;
  opacity: 0.8;
  color: #fff;
}

.table-scroll {
  overflow-x: auto;
  padding-bottom: 6px;
}

.price-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 700px;
}

.price-table th,
.price-table td {
  border: 1px solid #333;
  padding: 8px 10px;
  font-size: 14px;
}

.price-table th {
  background-color: #0b0101;
  color: #cbbfbf;
  font-weight: bold;
  text-align: center;
}

.whatif-table td:nth-child(1) {
  text-align: left;
  width: 45%;
}

.whatif-table td:nth-child(2),
.whatif-table td:nth-child(3) {
  text-align: right;
}

.subtotal-row td {
  font-weight: bold;
  background: rgba(11, 1, 1, 0.35);
}

.emphasis-row td {
  color: #fff;
  font-size: 15px;
}

.notes-panel {
  margin-bottom: 12px;
}

.notes-title {
  font-weight: bold;
  font-size: 16px;
  margin-bottom: 6px;
}

.notes-body {
  font-size: 14px;
  line-height: 1.35;
  white-space: pre-wrap;
}
</style>